import React, { useMemo, useState } from 'react';
import { CheckIcon, ChevronDownIcon, SearchIcon } from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import { RunningToolTitle } from '@cotera/client/v0/chat/running-tool-title';
import type { ToolRendererProps } from './index';
import { Favicon } from './web-favicon';
import { ToolErrorRow, toolErrorText } from './tool-error-row';
import {
  cleanString,
  formatCount,
  getHostname,
} from './web-tool-renderer-utils';

/** Results shown before the "Show N more" affordance. */
const DEFAULT_VISIBLE_RESULTS = 5;

type WebSearchInput = {
  q?: string | null;
};

type WebSearchResult = {
  position?: number | null;
  title?: string | null;
  link?: string | null;
  snippet?: string | null;
  source?: string | null;
  date?: string | null;
};

type WebSearchOutput = {
  results?: WebSearchResult[] | null;
};

/**
 * Renderer for the `search-api/google-search` web search tool. Shows a
 * shimmering status line while running and a compact, expandable list of
 * organic results once complete.
 */
export const WebSearchToolRenderer: React.FC<ToolRendererProps> = ({
  part,
}) => {
  const input = part.input as WebSearchInput | undefined;
  const query = useMemo(() => cleanString(input?.q), [input]);

  if (part.isIncomplete()) {
    return (
      <div className="flex items-center gap-2 py-1 text-sm text-muted-foreground">
        <SearchIcon className="h-4 w-4 shrink-0" />
        <RunningToolTitle className="italic">
          {query !== undefined
            ? `Searching the web for “${query}”`
            : 'Searching the web'}
        </RunningToolTitle>
      </div>
    );
  }

  if (part.isError()) {
    return (
      <ToolErrorRow
        title={
          query !== undefined
            ? `Search for “${query}” encountered an error`
            : 'Web search encountered an error'
        }
        errorText={toolErrorText(part)}
        input={part.input}
        icon={<SearchIcon className="h-4 w-4 shrink-0" />}
      />
    );
  }

  const results = (part.getOutput<WebSearchOutput>()?.results ?? []).filter(
    (result): result is WebSearchResult =>
      result !== null && result !== undefined
  );

  return <WebSearchResultCard query={query} results={results} />;
};

const WebSearchResultCard: React.FC<{
  query: string | undefined;
  results: WebSearchResult[];
}> = ({ query, results }) => {
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const hasResults = results.length > 0;
  const visible = showAll ? results : results.slice(0, DEFAULT_VISIBLE_RESULTS);
  const remaining = results.length - visible.length;

  const doneLabel =
    query !== undefined
      ? `Searched the web for “${query}”`
      : 'Searched the web';

  return (
    <div className="flex flex-col gap-2 py-1 text-sm">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        disabled={!hasResults}
        aria-expanded={hasResults ? expanded : undefined}
        className={cn(
          'group flex w-full items-center justify-between gap-4 text-left text-muted-foreground focus:outline-none',
          hasResults && 'hover:text-foreground',
          !hasResults && 'cursor-default'
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          <SearchIcon className="h-4 w-4 shrink-0" />
          <CheckIcon className="h-4 w-4 shrink-0 text-success" />
          <span className="truncate italic">{doneLabel}</span>
          {hasResults ? (
            <span className="shrink-0 text-xs text-muted-foreground/80">
              · {formatCount(results.length, 'result')}
            </span>
          ) : null}
        </span>
        {hasResults ? (
          <ChevronDownIcon
            className={cn(
              'h-4 w-4 shrink-0 transition-transform',
              expanded ? 'rotate-180' : 'rotate-0'
            )}
          />
        ) : null}
      </button>

      {!hasResults ? (
        <p className="text-sm text-muted-foreground">No results found.</p>
      ) : expanded ? (
        <div className="rounded-lg border border-border bg-card p-3">
          <ol className="flex flex-col gap-3">
            {visible.map((result, index) => (
              <WebSearchResultRow
                key={`${result.link ?? 'result'}-${index}`}
                result={result}
              />
            ))}
            {remaining > 0 ? (
              <li>
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  className="text-xs font-medium text-muted-foreground hover:text-foreground focus:outline-none"
                >
                  Show {remaining} more
                </button>
              </li>
            ) : null}
          </ol>
        </div>
      ) : null}
    </div>
  );
};

const WebSearchResultRow: React.FC<{ result: WebSearchResult }> = ({
  result,
}) => {
  const link = cleanString(result.link);
  const host = getHostname(link);
  const title = cleanString(result.title) ?? host ?? link ?? 'Untitled result';
  const source = cleanString(result.source) ?? host;
  const date = cleanString(result.date);
  const snippet = cleanString(result.snippet);

  return (
    <li className="flex gap-2.5">
      <Favicon url={link} className="mt-0.5 h-4 w-4 shrink-0 rounded-sm" />
      <div className="min-w-0 flex-1 space-y-0.5">
        {link !== undefined ? (
          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            className="line-clamp-1 font-medium text-foreground hover:underline"
          >
            {title}
          </a>
        ) : (
          <div className="line-clamp-1 font-medium text-foreground">
            {title}
          </div>
        )}
        {source !== undefined || date !== undefined ? (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {source !== undefined ? (
              <span className="truncate">{source}</span>
            ) : null}
            {source !== undefined && date !== undefined ? (
              <span aria-hidden>·</span>
            ) : null}
            {date !== undefined ? (
              <span className="shrink-0">{date}</span>
            ) : null}
          </div>
        ) : null}
        {snippet !== undefined ? (
          <p className="line-clamp-2 text-muted-foreground">{snippet}</p>
        ) : null}
      </div>
    </li>
  );
};
